import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, Chip, Divider,
} from '@mui/material';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';
import { sanitizeLoginSignInTermsHtml } from 'utils/sanitizeLoginSignInTermsHtml';

interface SSOProviderRecord {
    id: string;
    buttonName: string;
    providerType: string;
    loginPageHashPath: string;
    clientId?: string;
    clientSecret?: string;
    scopes?: string;
    authorizationUrl?: string;
    tokenUrl?: string;
    prompt?: string;
}

interface DomainRecord {
    _id: string;
    domainKey: string;
    apiBaseUrl: string;
    brandLogo: { name: string; path: string };
    minifiedBrandLogo: { name: string; path: string };
    themeBrandLogos?: { hasLight: boolean; hasDark: boolean } | {
        light?: { contentType: string; dataBase64: string };
        dark?: { contentType: string; dataBase64: string };
    };
    menuKey: string;
    theme?: string;
    uiDictionaryKey?: 'campaignKing' | 'instantUpdate' | 'anamnesis' | 'infralytiqs';
    supportEmail: string;
    contactPhone: string;
    contactEmail: string;
    logLevel: string;
    facebookAppId: string;
    facebookApiVersion: string;
    fbEmbeddedRedirectUri: string;
    googleMapsBrowserJSApiKey: string;
    loginPageHashPath: string;
    loginSignInTermsNotice?: string;
    userSignUpEnabled?: boolean;
    ssoSignUpEnabled?: boolean;
    ssoSignUp?: {
        licenseType?: string;
        module?: string;
        duration?: string;
    };
    managePlan?: boolean;
    assetsS3CdnUrl?: string;
    ssoProviders?: SSOProviderRecord[];
    infralytiqs?: {
        tenantId?: string;
        siteId?: string;
        dbName?: string;
        captureLocation?: boolean;
    };
    beamOnScreenInfralytiqs?: {
        tenantId?: string;
        siteId?: string;
    };
    createdBy?: string;
    modifiedBy?: string;
    createdAt?: string;
    updatedAt?: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
    domain: DomainRecord | null;
}

const uiDictionaryDisplay = (
    stored: DomainRecord['uiDictionaryKey'],
    menuKey: string,
): string => {
    if (stored === 'campaignKing') return 'Campaign King wording';
    if (stored === 'instantUpdate') return 'Instant Update wording';
    if (stored === 'anamnesis') return 'Anamnesis wording';
    if (stored === 'infralytiqs') return 'Infralytiqs wording';
    const mk =
        menuKey === 'campaignKing'
        || menuKey === 'instantUpdate'
        || menuKey === 'anamnesis'
        || menuKey === 'infralytiqs'
            ? menuKey
            : menuKey || '—';
    return `Follows Menu Key (${mk})`;
};

const Field: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
    <Box mb={1}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2">{value || '-'}</Typography>
    </Box>
);

const getHostnameFromUrl = (url: string): string => {
    try {
        return new URL(url).hostname;
    } catch {
        return url;
    }
};

export const DomainViewDialog: React.FC<Props> = ({ open, onClose, domain }) => {
    const [logoDetail, setLogoDetail] = useState<{
        light?: { contentType: string; dataBase64: string };
        dark?: { contentType: string; dataBase64: string };
    } | null>(null);

    useEffect(() => {
        if (!open || !domain?._id) {
            setLogoDetail(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const r = await apiClient.get(`/domain/${domain._id}`);
                if (cancelled) return;
                const t = r.data?.themeBrandLogos as DomainRecord['themeBrandLogos'];
                if (t && 'light' in t) {
                    setLogoDetail(t as { light?: { contentType: string; dataBase64: string }; dark?: { contentType: string; dataBase64: string } });
                } else {
                    setLogoDetail(null);
                }
            } catch (e) {
                Logger.error('DomainViewDialog', `Failed to load logos: ${e}`);
                setLogoDetail(null);
            }
        })();
        return () => { cancelled = true; };
    }, [open, domain?._id]);

    if (!domain) return null;

    const apiHostname = getHostnameFromUrl(domain.apiBaseUrl);
    const meta = domain.themeBrandLogos && 'hasLight' in domain.themeBrandLogos
        ? domain.themeBrandLogos
        : null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    Domain Details
                    <Chip label={domain.menuKey} size="small" color="primary" sx={{ fontWeight: 600 }} />
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Core Settings</Typography>
                <Box display="flex" gap={4} flexWrap="wrap" mb={2}>
                    <Field label="Domain Key" value={domain.domainKey} />
                    <Field label="API Base URL" value={domain.apiBaseUrl} />
                    <Field label="Assets S3 CDN URL" value={domain.assetsS3CdnUrl} />
                    <Field label="Menu Key" value={domain.menuKey} />
                    <Field label="Theme" value={domain.theme === 'professional' ? 'Professional' : 'Default (Light / Dark)'} />
                    <Field label="UI labels dictionary" value={uiDictionaryDisplay(domain.uiDictionaryKey, domain.menuKey)} />
                    <Field label="Log Level" value={domain.logLevel} />
                    <Field label="Login Page Hash Path" value={domain.loginPageHashPath} />
                    <Field
                        label="User Sign Up on login page"
                        value={domain.userSignUpEnabled === false ? 'Disabled' : 'Enabled'}
                    />
                    <Field
                        label="SSO Signup (auto-provision)"
                        value={domain.ssoSignUpEnabled === true ? 'Enabled' : 'Disabled'}
                    />
                    {domain.ssoSignUpEnabled === true && (
                        <>
                            <Field label="SSO Signup License Type" value={domain.ssoSignUp?.licenseType} />
                            <Field label="SSO Signup Module" value={domain.ssoSignUp?.module} />
                            <Field
                                label="SSO Signup Duration"
                                value={
                                    domain.ssoSignUp?.duration === 'infinite'
                                        ? 'Infinite'
                                        : domain.ssoSignUp?.duration
                                            ? `${domain.ssoSignUp.duration} month${domain.ssoSignUp.duration === '1' ? '' : 's'}`
                                            : undefined
                                }
                            />
                        </>
                    )}
                    <Field
                        label="Manage Plan menu"
                        value={domain.managePlan === false ? 'Hidden' : 'Visible'}
                    />
                </Box>
                <Box mb={2}>
                    <Typography variant="caption" color="text.secondary" display="block">Sign In — terms notice (login page)</Typography>
                    {domain.loginSignInTermsNotice?.trim() ? (
                        <Box
                            sx={{
                                typography: 'body2',
                                mt: 0.5,
                                wordBreak: 'break-word',
                                '& p': { mb: 0.5, '&:last-child': { mb: 0 } },
                                '& a': { color: 'secondary.main', textDecoration: 'underline' },
                                '& ul, & ol': { pl: 2.5, my: 0.5 },
                            }}
                            dangerouslySetInnerHTML={{
                                __html: sanitizeLoginSignInTermsHtml(domain.loginSignInTermsNotice.trim()),
                            }}
                        />
                    ) : (
                        <Typography variant="body2" sx={{ mt: 0.5 }} color="text.secondary">
                            — (empty: app shows built-in default text)
                        </Typography>
                    )}
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>DNS Details</Typography>
                <Box mb={2} sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>{domain.domainKey}</Typography>
                        <Chip label="CNAME" size="small" variant="outlined" sx={{ mx: 1, fontSize: '0.7rem' }} />
                        <Typography variant="body2" fontWeight={600} fontFamily="monospace">portal.campaignking.ai</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>{apiHostname}</Typography>
                        <Chip label="CNAME" size="small" variant="outlined" sx={{ mx: 1, fontSize: '0.7rem' }} />
                        <Typography variant="body2" fontWeight={600} fontFamily="monospace">apigateway.campaignking.ai</Typography>
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Brand Logo</Typography>
                <Box display="flex" gap={4} flexWrap="wrap" mb={2}>
                    <Field label="Logo Name" value={domain.brandLogo?.name} />
                    <Field label="Logo Path" value={domain.brandLogo?.path} />
                    <Field label="Minified Logo Name" value={domain.minifiedBrandLogo?.name} />
                    <Field label="Minified Logo Path" value={domain.minifiedBrandLogo?.path} />
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Theme image logos</Typography>
                {meta && (
                    <Box display="flex" gap={4} flexWrap="wrap" mb={1}>
                        <Field label="Light theme image" value={meta.hasLight ? 'Configured' : '—'} />
                        <Field label="Dark theme image" value={meta.hasDark ? 'Configured' : '—'} />
                    </Box>
                )}
                <Box display="flex" flexWrap="wrap" gap={3} mb={2}>
                    {logoDetail?.light?.dataBase64 && (
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Light preview</Typography>
                            <Box component="img" alt=""
                                src={`data:${logoDetail.light.contentType};base64,${logoDetail.light.dataBase64}`}
                                sx={{ maxHeight: 72, maxWidth: 220, objectFit: 'contain', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5 }} />
                        </Box>
                    )}
                    {logoDetail?.dark?.dataBase64 && (
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Dark preview</Typography>
                            <Box component="img" alt=""
                                src={`data:${logoDetail.dark.contentType};base64,${logoDetail.dark.dataBase64}`}
                                sx={{ maxHeight: 72, maxWidth: 220, objectFit: 'contain', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5 }} />
                        </Box>
                    )}
                    {meta && !meta.hasLight && !meta.hasDark && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No theme images configured for this domain.
                        </Typography>
                    )}
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Contact Details</Typography>
                <Box display="flex" gap={4} flexWrap="wrap" mb={2}>
                    <Field label="Support Email" value={domain.supportEmail} />
                    <Field label="Contact Email" value={domain.contactEmail} />
                    <Field label="Contact Phone" value={domain.contactPhone} />
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Third-Party</Typography>
                <Box display="flex" gap={4} flexWrap="wrap" mb={2}>
                    <Field label="Facebook App ID" value={domain.facebookAppId} />
                    <Field label="Facebook API Version" value={domain.facebookApiVersion} />
                </Box>
                <Box mb={2}>
                    <Field label="FB Embedded Redirect URI" value={domain.fbEmbeddedRedirectUri} />
                    <Field label="Google Maps API Key" value={domain.googleMapsBrowserJSApiKey} />
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Infralytiqs Tracker Bootstrap
                </Typography>
                <Box display="flex" gap={4} flexWrap="wrap" mb={2}>
                    <Field label="Tenant ID" value={domain.infralytiqs?.tenantId} />
                    <Field label="Site ID" value={domain.infralytiqs?.siteId} />
                    <Field label="DB Name" value={domain.infralytiqs?.dbName} />
                    <Field
                        label="Capture Precise Location"
                        value={domain.infralytiqs?.captureLocation ? 'Enabled (opt-in)' : 'Disabled'}
                    />
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Beam on Screen playback
                </Typography>
                <Box display="flex" gap={4} flexWrap="wrap" mb={2}>
                    <Field label="Tenant ID" value={domain.beamOnScreenInfralytiqs?.tenantId} />
                    <Field label="Site ID" value={domain.beamOnScreenInfralytiqs?.siteId} />
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>SSO Providers</Typography>
                {domain.ssoProviders && domain.ssoProviders.length > 0 ? (
                    domain.ssoProviders.map((prov, idx) => {
                        const apiBase = (domain.apiBaseUrl || '').replace(/\/+$/, '');
                        const redirectUri = `${apiBase}/auth/sso/${prov.id}/callback`;
                        return (
                            <Box key={idx} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 1.5 }}>
                                <Box display="flex" gap={4} flexWrap="wrap" mb={1}>
                                    <Field label="ID" value={prov.id} />
                                    <Field label="Button Name" value={prov.buttonName} />
                                    <Field label="Provider Type" value={prov.providerType} />
                                    <Field label="Login Hash Path" value={prov.loginPageHashPath} />
                                </Box>
                                <Box display="flex" gap={4} flexWrap="wrap" mb={1}>
                                    <Field label="Client ID" value={prov.clientId ? `${prov.clientId.substring(0, 12)}...` : '-'} />
                                    <Field label="Authorization URL" value={prov.authorizationUrl} />
                                    <Field label="Token URL" value={prov.tokenUrl} />
                                    {prov.prompt && <Field label="Prompt" value={prov.prompt} />}
                                </Box>
                                <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
                                    <Typography variant="caption" color="text.secondary">Redirect URI (for OAuth provider configuration)</Typography>
                                    <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>{redirectUri}</Typography>
                                </Box>
                            </Box>
                        );
                    })
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No SSO providers configured.
                    </Typography>
                )}

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Audit</Typography>
                <Box display="flex" gap={4} flexWrap="wrap">
                    <Field label="Created By" value={domain.createdBy} />
                    <Field label="Modified By" value={domain.modifiedBy} />
                    <Field label="Created At" value={domain.createdAt ? new Date(domain.createdAt).toLocaleString() : undefined} />
                    <Field label="Updated At" value={domain.updatedAt ? new Date(domain.updatedAt).toLocaleString() : undefined} />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" color="secondary" onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};
